import { Form, FormEvents } from "@dhx/ts-form";
import { Window } from "@dhx/ts-window";
import locale from "../locales/en";
import { Popup, PopupEvents } from "@dhx/ts-popup";
import { ICellMeta, ILink, SpreadsheetEvents } from "../types";
import { Toolbar } from "@dhx/ts-toolbar";
import { getCellNode, isURL } from "../helpers/main";
import { Grid } from "@dhx/ts-grid";
import { NavigationBarEvents } from "@dhx/ts-navbar";
import { message } from "@dhx/ts-message";
import { IEventSystem } from "@dhx/ts-common/events";
import { awaitRedraw } from "@dhx/ts-common/dom";

export class LinkForm {
	protected _handlers: { [key: string]: (...args) => void };
	private _form: Form;
	private _modal: Window;
	private _cell: string;
	private _debounds: any;
	private _popup: Popup;
	private _toolbar: Toolbar;
	private _grid: Grid;
	private _toolbarLink: string | null;
	private _cellMeta: ICellMeta | null;
	private _set: (cell: string, link?: ILink) => void;
	private _events: IEventSystem<SpreadsheetEvents>;

	constructor(grid, set, events) {
		this._set = set;
		this._events = events;
		this._grid = grid;
		this._modal = new Window({
			title: `${locale.insert} ${locale.link.toLowerCase()}`,
			movable: true,
			closable: true,
			css: "dhx-spreadsheet-form-link",
			height: 300,
			width: 300,
		});
		this._form = new Form("", {
			padding: 15,
			rows: [
				{
					name: "text",
					type: "input",
					label: locale.text,
					labelPosition: "top",
					width: 270,
					placeholder: locale.text,
				},
				{
					name: "href",
					type: "input",
					label: locale.link,
					labelPosition: "top",
					width: 270,
					placeholder: locale.link,
				},
				{
					align: "end",
					css: "dhx-button-wrapper",
					cols: [
						{
							type: "button",
							text: locale.cancel,
							id: "cancel",
							view: "link",
						},
						{
							type: "button",
							text: locale.save,
							id: "save",
						},
					],
				},
			],
		});
		this._popup = new Popup({
			css: "dhx-spreadsheet-form-link dhx_editor-popup",
		});
		this._toolbar = new Toolbar(undefined, {
			css: "dhx_link-toolbar",
			data: [
				{
					type: "customHTML",
					html: "",
					css: "dhx_spreadsheet_link href-link",
					id: "link",
				},
				{
					icon: "dxi dxi-file-outline",
					tooltip: locale.copy.toLowerCase(),
					circle: true,
					id: "copy",
				},
				{ icon: "dxi dxi-pencil", tooltip: locale.edit.toLowerCase(), circle: true, id: "edit" },
				{
					icon: "dxi dxi-link-variant-off",
					tooltip: `${locale.remove} ${locale.link}`.toLowerCase(),
					circle: true,
					id: "unlink",
				},
			],
		});
		this._modal.attach(this._form);
		this._popup.attach(this._toolbar);
		this._initHandlers();
	}

	hide() {
		if (this._modal.isVisible()) {
			this._modal.hide();
		}
		this._form.setValue({
			text: "",
			href: "",
		});
		this._debounds = false;
		this._events.fire(SpreadsheetEvents.afterPopupHide);
	}

	isVisible() {
		return this._modal.isVisible();
	}

	destructor() {
		this._form.destructor();
		this._modal.destructor();
		this._popup.destructor();
		this._toolbar.destructor();
	}

	show(cell: string, cellMeta: ICellMeta) {
		if (!this._modal.isVisible()) {
			this._modal.show();
		}
		this._cell = cell;
		const { value, link } = cellMeta;
		const { href, text } = (link as ILink) || {};
		const hrefVal = !href && isURL(value) ? value : href;

		this._form.setValue({
			text: text || value,
			href: hrefVal,
		});

		this.toogleSaveButton(hrefVal?.toString() || "");
		awaitRedraw().then(() => {
			this.hideInfo();
		});
	}

	showInfo(cell: string, cellMeta: ICellMeta) {
		if (this._popup.isVisible()) return;
		this._cell = cell;
		this._cellMeta = cellMeta;
		const { link, value, locked = false } = cellMeta;
		let href = link?.href;
		if (!href) {
			const splited = `${value}`.split(" ");
			href = splited.find(item => isURL(item));
		}
		this.toogleLocked(locked);
		if (!href) return;
		this._toolbarLink = href;
		this._toolbar.data.update("link", { html: `<a href="${href}" target="blank">${href}</a>` });
		this._popup.show(getCellNode(cell, this._grid, true));
	}

	hideInfo() {
		if (!this._popup.isVisible()) return;
		this._popup.hide();
		this._toolbarLink = null;
		this._cellMeta = null;
		this._events.fire(SpreadsheetEvents.afterPopupHide);
	}

	private _keyHandler(e: KeyboardEvent) {
		if (e.code === "Escape") {
			e.preventDefault();
			this.hide();
		}

		if (e.code === "Enter") {
			e.preventDefault();
			this._form.blur("href");
			this._form.blur("text");
			if (!this._debounds) {
				this._debounds = setTimeout(() => {
					this._save();
					this._debounds = false;
				}, 200);
			}
		}
	}

	protected _save() {
		let { text, href } = this._form.getValue();
		if (!href.startsWith("http")) {
			href = `http://${href}`;
			this._form.setValue({ href });
		}
		if (!this._form.validate()) {
			this._form.setFocus("href");
			return;
		}
		this._set(this._cell, href ? { text, href } : undefined);
		this.hide();
	}
	
	protected toogleSaveButton (text: string) {
		const save = this._form.getItem("save");
			if (!text) {
				save.disable();
			} else {
				save.enable();
			}
	}

	protected toogleLocked (locked: boolean) {
		if (locked) {
				this._toolbar.disable(["edit", "unlink"])
			} else {
				this._toolbar.enable(["edit", "unlink"])
			}
	}

	protected _initHandlers() {
		this._modal.events.on(PopupEvents.afterShow, () => {
			this._form.setFocus("href");
		});
		this._form.events.on(FormEvents.click, name => {
			switch (name) {
				case "save":
					this._save();
					break;
				case "cancel":
				default:
					this.hide();
					break;
			}
		});
		this._form.events.on(
			"keydown",
			function(event) {
				this._keyHandler(event);
			}.bind(this)
		);
		this._form.getItem("href").events.on("input", text => {
			this.toogleSaveButton(text);
		});
		this._toolbar.events.on(NavigationBarEvents.click, id => {
			switch (id) {
				case "copy":
					if ((window as any).clipboardData) {
						(window as any).clipboardData.setData("text/plain", this._toolbarLink);
					} else {
						navigator.clipboard.writeText(this._toolbarLink as string);
					}
					message({
						text: locale.linkCopied,
						css: "dhx_message--success",
						expire: 2000,
					});

					break;
				case "edit":
					this.show(this._cell, this._cellMeta as ICellMeta);
					break;
				case "unlink":
					this._set(this._cell);
					break;
			}
			this.hideInfo();
		});
	}
}
